/**
 * Alias parsing utilities for extracting template layer aliases from Alt text
 */

// Exact regex as specified in requirements
const ALIAS_RE = /alias[:=\s]+([a-z0-9_]+(?:__[a-z0-9_]+)?)/i;

/**
 * Parse alias from Alt text using the specified regex
 * Supports formats: 'alias:front__title', 'ALIAS=spine__bg', 'alias front__photo1'
 * @param alt - Alt text from element
 * @returns Parsed alias or null if not found
 */
export function parseAlias(alt?: string): string | null {
  if (!alt) return null;

  // Use the exact regex specified in requirements
  const match = ALIAS_RE.exec(alt);
  return match?.[1] ?? null; // e.g. 'front__title'
}

/**
 * Determine surface from alias prefix
 * @param alias - Parsed alias (e.g. 'front__title')
 * @returns Surface type or undefined if unknown
 */
export function surfaceOf(alias: string): 'front' | 'spine' | 'back' | undefined {
  if (alias.startsWith('front__')) return 'front';
  if (alias.startsWith('spine__')) return 'spine';
  if (alias.startsWith('back__')) return 'back';
  return undefined;
}

/**
 * Validate alias format
 * @param alias - Alias to validate
 * @returns True if alias has valid format
 */
export function isValidAlias(alias: string): boolean {
  return /^(front|spine|back)__[a-z0-9_]+$/i.test(alias);
}

/**
 * Extract all aliases from a collection of Alt texts
 * @param altTexts - Array of Alt text strings
 * @returns Array of parsed aliases with their original Alt text
 */
export function extractAliases(altTexts: string[]): Array<{ alias: string; altText: string; surface: string }> {
  const results: Array<{ alias: string; altText: string; surface: string }> = [];
  
  for (const altText of altTexts) {
    const alias = parseAlias(altText);
    if (alias && isValidAlias(alias)) {
      const surface = surfaceOf(alias);
      if (surface) {
        results.push({ alias, altText, surface });
      }
    }
  }
  
  return results;
}

/**
 * Group aliases by surface
 * @param aliases - Array of alias objects
 * @returns Object with aliases grouped by surface
 */
export function groupAliasesBySurface(aliases: Array<{ alias: string; surface: string }>) {
  const grouped: Record<string, string[]> = {
    front: [],
    spine: [],
    back: []
  };
  
  for (const { alias, surface } of aliases) {
    if (grouped[surface]) {
      grouped[surface].push(alias);
    }
  }
  
  return grouped;
}

/**
 * Log alias parsing results for debugging
 * @param altTexts - Array of Alt text strings to analyze
 * @param prefix - Log prefix
 */
export function debugAliases(altTexts: string[], prefix = '[debug]'): void {
  console.log(`${prefix} Analyzing ${altTexts.length} Alt texts for aliases...`);
  
  const aliases = extractAliases(altTexts);
  
  if (aliases.length === 0) {
    console.warn(`${prefix} No valid aliases found. Use format: alias:front__title`);
    return;
  }
  
  const grouped = groupAliasesBySurface(aliases);
  
  console.log(`${prefix} Found ${aliases.length} valid aliases:`);
  for (const surface of ['front', 'spine', 'back']) {
    if (grouped[surface].length > 0) {
      console.log(`${prefix}   ${surface}: [${grouped[surface].join(', ')}]`);
    }
  }
}
